import * as pdfjs from 'pdfjs-dist'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { TextItem } from 'pdfjs-dist/types/src/display/api'
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'
import { supabase } from './supabase'
import { base } from './db'
import type { DocumentChantier, Feuillet } from './types'

pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker()

// ---------------------------------------------------------------------
// Ouverture d'un plan, avec copie conservée sur l'appareil
// ---------------------------------------------------------------------

/**
 * Le fichier est téléchargé en entier une seule fois, puis gardé dans la base
 * locale de l'appareil (IndexedDB). Les ouvertures suivantes sont instantanées
 * et fonctionnent sans réseau.
 */
export async function ouvrirPlan(
  doc: DocumentChantier,
  progression?: (fraction: number) => void
): Promise<PDFDocumentProxy> {
  let octets = (await base.plans.get(doc.fichier_path))?.octets
  if (!octets) {
    if (!navigator.onLine) {
      throw new Error(
        "Ce plan n'a pas encore été ouvert sur cet appareil et il n'y a pas de réseau."
      )
    }
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(doc.fichier_path, 60 * 30)
    if (error) throw error
    if (!data?.signedUrl) throw new Error('Fichier introuvable.')
    octets = await telecharger(data.signedUrl, progression)
    try {
      await base.plans.put({ cle: doc.fichier_path, octets, conserve_le: Date.now() })
    } catch {
      // Espace insuffisant sur l'appareil : on ouvre quand même, sans copie locale.
    }
  }
  // pdf.js prend possession du tampon : on lui passe une copie.
  return pdfjs.getDocument({ data: new Uint8Array(octets.slice(0)) }).promise
}

async function telecharger(url: string, progression?: (f: number) => void): Promise<ArrayBuffer> {
  const rep = await fetch(url)
  if (!rep.ok || !rep.body) throw new Error(`Téléchargement impossible (${rep.status}).`)
  const total = Number(rep.headers.get('content-length')) || 0
  const lecteur = rep.body.getReader()
  const morceaux: Uint8Array[] = []
  let recu = 0
  for (;;) {
    const { done, value } = await lecteur.read()
    if (done) break
    morceaux.push(value)
    recu += value.length
    if (total && progression) progression(recu / total)
  }
  const tout = new Uint8Array(recu)
  let pos = 0
  for (const m of morceaux) {
    tout.set(m, pos)
    pos += m.length
  }
  return tout.buffer
}

// ---------------------------------------------------------------------
// Feuillets (index page par page)
// ---------------------------------------------------------------------

export async function chargerFeuillets(documentId: string): Promise<Feuillet[]> {
  const { data, error } = await supabase
    .from('feuillets')
    .select('*')
    .eq('document_id', documentId)
    .order('page')
  if (error) throw error
  return (data as Feuillet[]) ?? []
}

export async function enregistrerFeuillets(rangs: Feuillet[]): Promise<void> {
  if (rangs.length === 0) return
  const { error } = await supabase
    .from('feuillets')
    .upsert(
      rangs.map((r) => ({ ...r, modifie_le: new Date().toISOString() })),
      { onConflict: 'document_id,page' }
    )
  if (error) throw error
}

/** Libellé affiché pour une page : « A304 — COMPOSITION TYPE », ou « Page 50 ». */
export function libelle(f: Feuillet | undefined, page: number): string {
  if (!f || (!f.code && !f.titre)) return `Page ${page}`
  if (f.code && f.titre) return `${f.code} — ${f.titre}`
  return f.code ?? f.titre ?? `Page ${page}`
}

// ---------------------------------------------------------------------
// Détection automatique dans la cartouche
// ---------------------------------------------------------------------
//
// Principe : dans un jeu de plans, la cartouche est au même endroit sur
// toutes les pages. On ne devine donc pas page par page : on cherche
// l'emplacement où un code de feuillet (A100, A101…) apparaît sur presque
// toutes les pages avec une valeur différente chaque fois. C'est la case
// « numéro de feuillet ». Le titre est cherché de la même façon, par sa
// position par rapport au numéro. La liste des dessins (souvent en page de
// garde) sert à compléter ce qui manque.

interface Morceau {
  texte: string
  x: number // gauche, en fraction de la largeur (0 à 1)
  y: number // ligne de base, en fraction de la hauteur (0 = haut)
  h: number // hauteur des caractères, en fraction de la hauteur
  fin: number // droite estimée, en fraction de la largeur
}

interface TextePage {
  morceaux: Morceau[]
  lignes: Morceau[]
  ocr?: boolean
}

/** Codes de feuillet usuels : A304, A-304, A3.04, S-101, M1.01a, AR-201… */
const RE_CODE = /^[A-Z]{1,3}[-.]?\d{1,3}(?:[.-]\d{1,3})?[A-Z]?$/i

/** « A R C H I T E C T U R E » → « ARCHITECTURE » */
const recoller = (s: string) => (/^(\S )+\S$/.test(s) ? s.replace(/ /g, '') : s)
const compact = (s: string) => recoller(s).replace(/\s+/g, '').toUpperCase()
const aDesLettres = (s: string) => /[a-zà-ÿ]{3}/i.test(s)
const cle = (t: string) => t.toLowerCase().replace(/\s+/g, ' ').trim()

/** Un code plausible : au moins deux chiffres, et pas une révision (R8, R10). */
function estCode(s: string): string | null {
  const c = compact(s)
  if (c.length > 10 || !RE_CODE.test(c)) return null
  if ((c.match(/\d/g) ?? []).length < 2) return null // grilles : F.2, A.3, B6…
  if (/^(R|REV)\d+$/.test(c)) return null
  return c
}

async function textePage(pdf: PDFDocumentProxy, n: number): Promise<TextePage> {
  const page = await pdf.getPage(n)
  const vue = page.getViewport({ scale: 1 })
  const contenu = await page.getTextContent()
  const W = vue.width
  const H = vue.height

  const morceaux: Morceau[] = (contenu.items as TextItem[])
    .filter((i) => typeof i.str === 'string' && i.str.trim() !== '')
    .map((i) => {
      // Coordonnées ramenées à la page telle qu'elle s'affiche (rotation comprise).
      const [x, y] = vue.convertToViewportPoint(i.transform[4], i.transform[5])
      const h = Math.hypot(i.transform[2], i.transform[3])
      return {
        texte: recoller(i.str.trim()),
        x: x / W,
        y: y / H,
        h: h / H,
        fin: (x + Math.abs(i.width)) / W
      }
    })
  page.cleanup()

  return { morceaux, lignes: regrouperLignes(morceaux) }
}

/** Lignes : morceaux voisins sur la même ligne de base et de même taille. */
function regrouperLignes(morceaux: Morceau[], ocr = false): Morceau[] {
  // Les hauteurs mesurées par l'OCR varient plus que celles du texte natif.
  const tolH = ocr ? 0.45 : 0.2
  const tolY = ocr ? 0.6 : 0.35
  const tolEcart = ocr ? 1.6 : 1.2
  const tri = [...morceaux].sort((a, b) => a.y - b.y || a.x - b.x)
  const lignes: Morceau[] = []
  for (const m of tri) {
    const d = lignes[lignes.length - 1]
    if (
      d &&
      Math.abs(d.y - m.y) < Math.max(d.h, m.h) * tolY &&
      Math.abs(d.h - m.h) / Math.max(d.h, m.h) < tolH &&
      m.x >= d.x &&
      m.x - d.fin < Math.max(d.h, m.h) * tolEcart
    ) {
      d.texte += ' ' + m.texte
      d.fin = Math.max(d.fin, m.fin)
    } else {
      lignes.push({ ...m })
    }
  }
  for (const l of lignes) l.texte = l.texte.replace(/\s+/g, ' ').trim()
  return lignes
}

/** Trouve la case « numéro de feuillet » commune à toutes les pages. */
function trouverCaseCode(pages: TextePage[]) {
  const cands: { p: number; code: string; x: number; y: number; h: number }[] = []
  pages.forEach((t, i) =>
    t.morceaux.forEach((m) => {
      const code = estCode(m.texte)
      if (code) cands.push({ p: i, code, x: m.x, y: m.y, h: m.h })
    })
  )
  let meilleur: (typeof cands)[number] | null = null
  let note = 0
  for (const a of cands) {
    const pagesVues = new Set<number>()
    for (const b of cands) {
      if (b.p !== a.p && b.code !== a.code && Math.abs(b.x - a.x) < 0.012 && Math.abs(b.y - a.y) < 0.012) {
        pagesVues.add(b.p)
      }
    }
    // À égalité, le plus gros texte l'emporte.
    const s = pagesVues.size + a.h
    if (s > note) {
      note = s
      meilleur = a
    }
  }
  return { cands, ancre: meilleur, appui: Math.floor(note) }
}

/** Lit la liste des dessins (code → titre) si le jeu en contient une. */
function lireListeDesDessins(pages: TextePage[]): { ordre: string[]; titres: Map<string, string> } {
  const ordre: string[] = []
  const titres = new Map<string, string>()
  for (const t of pages) {
    const codes = t.morceaux
      .map((m) => ({ m, code: estCode(m.texte) }))
      .filter((c): c is { m: Morceau; code: string } => c.code !== null)
    // Une liste, c'est au moins six codes alignés dans une même colonne.
    const colonnes = new Map<number, typeof codes>()
    for (const c of codes) {
      const k = Math.round(c.m.x * 300)
      colonnes.set(k, [...(colonnes.get(k) ?? []), c])
    }
    for (const col of colonnes.values()) {
      if (col.length < 6) continue
      col.sort((a, b) => a.m.y - b.m.y)
      for (const { m, code } of col) {
        if (!titres.has(code)) {
          const voisin = t.morceaux
            .filter(
              (v) =>
                v !== m &&
                Math.abs(v.y - m.y) < m.h * 0.4 &&
                v.x > m.x &&
                v.x < m.x + 0.15 &&
                aDesLettres(v.texte)
            )
            .sort((a, b) => a.x - b.x)[0]
          if (voisin) titres.set(code, voisin.texte)
        }
        if (!ordre.includes(code)) ordre.push(code)
      }
    }
  }
  return { ordre, titres }
}

function choisirCodes(pages: TextePage[]) {
  const total = pages.length
  const { cands, ancre, appui } = trouverCaseCode(pages)
  const caseFiable = ancre !== null && appui >= Math.max(1, Math.min(total - 1, total * 0.3))

  const codes: (string | null)[] = []
  const positions: ({ x: number; y: number } | null)[] = []
  for (let i = 0; i < total; i++) {
    let choix: (typeof cands)[number] | undefined
    if (caseFiable && ancre) {
      choix = cands
        .filter((c) => c.p === i && Math.abs(c.x - ancre.x) < 0.03 && Math.abs(c.y - ancre.y) < 0.03)
        .sort(
          (a, b) => Math.hypot(a.x - ancre.x, a.y - ancre.y) - Math.hypot(b.x - ancre.x, b.y - ancre.y)
        )[0]
    } else {
      // Document trop court ou sans cartouche régulière : le plus gros code,
      // de préférence en bas à droite.
      choix = cands
        .filter((c) => c.p === i)
        .sort((a, b) => b.h * (1 + b.x + b.y) - a.h * (1 + a.x + a.y))[0]
    }
    codes.push(choix?.code ?? null)
    positions.push(choix ? { x: choix.x, y: choix.y } : null)
  }
  return { codes, positions, caseFiable }
}

/**
 * Lit la cartouche de chaque page et propose un code et un titre.
 * Les pages déjà corrigées à la main ne sont pas touchées.
 */
export async function detecterFeuillets(
  pdf: PDFDocumentProxy,
  documentId: string,
  existants: Feuillet[],
  progression: (fait: number, total: number) => void,
  annule: () => boolean,
  enregistrer: (rangs: Feuillet[]) => Promise<void> = enregistrerFeuillets
): Promise<Feuillet[]> {
  const total = pdf.numPages
  const manuels = new Set(existants.filter((f) => f.source === 'manuel').map((f) => f.page))

  // 1. Le texte de chaque page.
  const pages: TextePage[] = []
  for (let n = 1; n <= total; n++) {
    if (annule()) return existants
    try {
      pages.push(await textePage(pdf, n))
    } catch {
      pages.push({ morceaux: [], lignes: [] })
    }
    progression(n * 0.3, total)
  }

  // 2. La case du numéro de feuillet, d'abord dans le texte du PDF.
  let { codes, positions, caseFiable } = choisirCodes(pages)

  // 2b. Les pages sans numéro lisible passent à la reconnaissance de texte
  //     (cartouche dessinée en traits, plan numérisé…).
  const aLire = pages.map((_, i) => i).filter((i) => !caseFiable || !codes[i])
  if (aLire.length > 0) {
    const { lireCartouche, fermerOcr } = await import('./ocr')
    try {
      for (let k = 0; k < aLire.length; k++) {
        if (annule()) return existants
        const i = aLire[k]
        // Coin bas-droit d'abord ; sinon la page est peut-être tournée
        // (cartouche à la verticale), ou la cartouche est plus haute.
        const essais = [
          {},
          { rotation: 90 },
          { rotation: 270 },
          { largeur: 0.3, hauteur: 1 }
        ]
        for (const essai of essais) {
          if (annule()) return existants
          try {
            const o = await lireCartouche(pdf, i + 1, essai)
            if (o.mots.some((m) => estCode(m.texte))) {
              pages[i] = { morceaux: o.mots, lignes: regrouperLignes(o.mots, true), ocr: true }
              break
            }
          } catch {
            // essai suivant
          }
        }
        progression(total * 0.3 + ((k + 1) / aLire.length) * total * 0.6, total)
      }
    } finally {
      await fermerOcr()
    }
    ;({ codes, positions, caseFiable } = choisirCodes(pages))
  }

  // 3. Les trous comblés par la suite logique : la liste des dessins donne
  //    l'ordre (A000 vient avant A001), sinon on laisse vide.
  const liste = lireListeDesDessins(pages)
  for (let i = 0; i < total; i++) {
    if (codes[i]) continue
    const avant = codes[i - 1]
    const apres = codes[i + 1]
    let devine: string | undefined
    if (avant && liste.ordre.includes(avant)) devine = liste.ordre[liste.ordre.indexOf(avant) + 1]
    else if (apres && liste.ordre.includes(apres)) devine = liste.ordre[liste.ordre.indexOf(apres) - 1]
    if (devine && !codes.includes(devine)) codes[i] = devine
  }

  // Texte répété dans la cartouche sur plus du tiers des pages : nom de la
  // firme, du projet, de la discipline… jamais un titre de feuillet. On ne
  // compte que la zone de la cartouche : un mot comme « EXTÉRIEUR » peut
  // revenir partout sur les dessins et faire quand même partie d'un titre.
  const dansCartouche = (l: Morceau, p: { x: number; y: number }) =>
    Math.abs(l.x - p.x) < 0.15 && l.y - p.y < 0.005 && l.y - p.y > -0.2
  const frequence = new Map<string, number>()
  pages.forEach((t, i) => {
    const p = positions[i]
    if (!p) return
    for (const k of new Set(t.lignes.filter((l) => dansCartouche(l, p)).map((l) => cle(l.texte)))) {
      frequence.set(k, (frequence.get(k) ?? 0) + 1)
    }
  })
  const seuil = Math.max(3, total / 3)
  const repete = (s: string) => (frequence.get(cle(s)) ?? 0) >= seuil

  // 4. La case du titre : la position, relative au numéro, où l'on trouve
  //    sur le plus de pages un texte différent d'une page à l'autre.
  // Tolérances plus larges si des pages ont été lues par OCR.
  const avecOcr = pages.some((t) => t.ocr)
  const tolTaille = avecOcr ? 0.5 : 0.15
  const tolDy = avecOcr ? 0.007 : 0.004
  const rel: { dx: number; dy: number; h: number; texte: string }[] = []
  pages.forEach((t, i) => {
    const p = positions[i]
    if (!p) return
    for (const l of t.lignes) {
      if (dansCartouche(l, p) && aDesLettres(l.texte) && !repete(l.texte)) {
        rel.push({ dx: l.x - p.x, dy: l.y - p.y, h: l.h, texte: l.texte })
      }
    }
  })
  let caseTitre: (typeof rel)[number] | null = null
  let noteTitre = 0
  for (const a of rel) {
    const textes = new Set<string>()
    for (const b of rel) {
      if (Math.abs(b.dx - a.dx) < 0.03 && Math.abs(b.dy - a.dy) < tolDy && Math.abs(b.h - a.h) / a.h < tolTaille) {
        textes.add(b.texte)
      }
    }
    if (textes.size > noteTitre) {
      noteTitre = textes.size
      caseTitre = a
    }
  }

  function titreCartouche(i: number): string | null {
    const p = positions[i]
    if (!p || !caseTitre || noteTitre < 2) return null
    const ct = caseTitre
    // Un titre peut tenir sur une, deux ou trois lignes, centrées : on prend
    // toutes les lignes de même taille dans la bande autour de la case.
    const bande = Math.max(0.016, ct.h * 2.2)
    const bloc = pages[i].lignes
      .filter(
        (l) =>
          Math.abs(l.x - (p.x + ct.dx)) < 0.05 &&
          Math.abs(l.y - (p.y + ct.dy)) < bande &&
          Math.abs(l.h - ct.h) / ct.h < tolTaille &&
          aDesLettres(l.texte) &&
          !repete(l.texte)
      )
      .sort((a, b) => a.y - b.y)
    if (bloc.length === 0) return null
    return bloc
      .slice(0, 3)
      .map((l) => l.texte)
      .join(' ')
      .replace(/\s+/g, ' ')
      .slice(0, 120)
  }

  // 5. Enregistrement par paquets.
  const resultat = new Map(existants.map((f) => [f.page, f]))
  let paquet: Feuillet[] = []
  for (let n = 1; n <= total; n++) {
    if (annule()) break
    if (!manuels.has(n)) {
      const code = codes[n - 1]
      const titre = titreCartouche(n - 1) ?? (code ? liste.titres.get(code) ?? null : null)
      const f: Feuillet = { document_id: documentId, page: n, code, titre, source: 'auto' }
      resultat.set(n, f)
      paquet.push(f)
    }
    if (paquet.length >= 40 || n === total) {
      await enregistrer(paquet)
      paquet = []
    }
    progression(total * 0.9 + n * 0.1, total)
  }
  return [...resultat.values()].sort((a, b) => a.page - b.page)
}
